/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext } from "react"
import { useController } from "react-hook-form"

import classNames from "classnames"

import { useMergedRefs } from "shared/lib/useMergedRefs"
import { Ref } from "shared/types/Ref"
import {
  CheckboxListControl,
  CheckboxListProps
} from "shared/ui/input/select/checkbox/CheckboxListControl"

import { Input as DefaultInput, InputContext } from "../../Input"
import { ReadonlySelect } from "../components/ReadonlySelect"
import { SelectValue } from "../components/SelectValue"

function Root(
  {
    className,
    ...props
  }: React.ComponentPropsWithoutRef<typeof DefaultInput.Root>,
  ref?: React.ForwardedRef<HTMLDivElement>
) {
  return (
    <DefaultInput.Root
      {...props}
      className={classNames("checkbox-list-input", className)}
      ref={ref}
    />
  )
}

const InputBase = React.memo(
  React.forwardRef(
    (
      {
        className,
        ...props
      }: Partial<CheckboxListProps<any>> & {
        options: CheckboxListProps<any>["options"]
      },
      externalRef: React.ForwardedRef<HTMLInputElement>
    ) => {
      const { inputRef } = useContext(InputContext)
      const { disabled, readonly, loading, label } = useContext(InputContext)

      const ref = useMergedRefs(externalRef, inputRef as Ref<HTMLInputElement>)

      if (readonly) {
        return null
      }

      return (
        <CheckboxListControl
          {...props}
          label={label || ""}
          aria-label={label}
          ref={ref}
          disabled={disabled}
          loading={loading}
          className={classNames("input__control", className)}
        />
      )
    }
  )
)

const HookFormInput = React.memo(
  React.forwardRef(
    (
      {
        className,
        ...props
      }: Partial<CheckboxListProps<any>> & {
        options: CheckboxListProps<any>["options"]
      },
      externalRef?: React.ForwardedRef<HTMLInputElement>
    ) => {
      const { name, required } = useContext(InputContext)
      const {
        field: { ref: registerRef, ...field }
      } = useController({
        name,
        rules: { required }
      })

      const ref = useMergedRefs(externalRef, registerRef)

      return <InputBase {...field} {...props} ref={ref} />
    }
  )
)

function InputComponent<ValueType>(
  props: Partial<CheckboxListProps<ValueType>> & {
    options: CheckboxListProps<ValueType>["options"]
  },
  ref?: React.ForwardedRef<HTMLInputElement>
) {
  const { name } = useContext(InputContext)

  if (name) {
    return <HookFormInput {...props} ref={ref} />
  }
  return <InputBase {...props} ref={ref} />
}

const Input = React.memo(
  React.forwardRef(InputComponent)
) as typeof InputComponent

const CheckboxList = {
  ...DefaultInput,
  Root: React.memo(React.forwardRef(Root)),
  Readonly: ReadonlySelect,
  Value: SelectValue,
  Input
}
export { CheckboxList }
